import React, { useEffect, useRef, useState } from 'react';
import { FaCheckCircle, FaCloudUploadAlt, FaDownload, FaExclamationCircle, FaFileCsv, FaSpinner, FaTimes } from 'react-icons/fa';
import './NotificationCsvImport.css';
import { apiRequest } from '../utils/auth';
import { CsvImportReport, downloadCsv, readRecipientCsv } from '../utils/notificationCsv';

const ISSUE_LABELS: Record<string, string> = {
  INVALID_IDENTIFIER: 'Invalid user ID or email.',
  EMPTY_IDENTIFIER: 'Missing user ID or email.',
  USER_NOT_FOUND: 'User not found.',
  AMBIGUOUS_EMAIL: 'Multiple accounts share this email. Use userId.',
  IDENTITY_MISMATCH: 'User ID and email do not match.',
};

interface Props {
  onReport: (report: CsvImportReport | null) => void;
  onBusyChange: (busy: boolean) => void;
}

export default function NotificationCsvImport({ onReport, onBusyChange }: Props) {
  const [report, setReport] = useState<CsvImportReport | null>(null);
  const [error, setError] = useState('');
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => { generation.current++; request.current?.abort(); }, []);

  const clear = () => {
    generation.current++; request.current?.abort();
    dragDepth.current = 0; setDragging(false);
    setReport(null); onReport(null); setError(''); setFilename(''); setBusy(false); onBusyChange(false);
  };
  const importFiles = async (files: File[]) => {
    if (!files.length) return;
    const file = files[0];
    const count = files.length;
    const current = ++generation.current;
    request.current?.abort();
    setReport(null); onReport(null); setError(''); setFilename(file.name); setBusy(true); onBusyChange(true);
    const controller = new AbortController(); request.current = controller;
    const timer = window.setTimeout(() => controller.abort(), 30000);
    try {
      if (count !== 1) throw new Error('Select exactly one CSV file.');
      const csv = await readRecipientCsv(file, controller.signal);
      if (current !== generation.current) return;
      const result = await apiRequest<{ success: boolean; report: CsvImportReport }>('/api/admin/notifications/import-csv', {
        method: 'POST', body: JSON.stringify({ csv }), signal: controller.signal,
      });
      if (current !== generation.current) return;
      if (!result.success || !result.report) throw new Error('CSV validation failed. Select the file again to retry.');
      setReport(result.report); onReport(result.report);
    } catch (err) {
      if (current === generation.current) setError(controller.signal.aborted ? 'CSV validation timed out. Retry; no audience was selected.' : err instanceof Error ? err.message : 'CSV import failed. Please retry.');
    } finally {
      window.clearTimeout(timer);
      if (current === generation.current) { setBusy(false); onBusyChange(false); }
    }
  };
  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // Allow reselecting the same corrected file.
    void importFiles(files);
  };
  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0; setDragging(false);
    void importFiles(Array.from(event.dataTransfer.files));
  };


  return <div className="notification-csv" aria-label="CSV recipients">
    <div className="notification-csv__toolbar">
      <span>Import recipients</span>
      <button className="notification-csv__link" type="button" onClick={() => downloadCsv('notification-recipients-template.csv', [['userId', 'email']])}>
        <FaDownload aria-hidden="true" /> Download template
      </button>
    </div>
    <div className={dragging ? 'notification-csv__drop is-dragging' : 'notification-csv__drop'}
      onDragEnter={event => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return;
        event.preventDefault(); dragDepth.current++; setDragging(true);
      }}
      onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
      onDragLeave={event => { event.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); }}
      onDrop={onDrop}
      data-testid="csv-drop-zone">
      <button type="button" className="notification-csv__upload" aria-label="Choose recipient CSV file" onClick={() => inputRef.current?.click()}>
        {busy ? <FaSpinner className="notification-csv__spinner" aria-hidden="true" /> : <FaCloudUploadAlt aria-hidden="true" />}
        <strong>{dragging ? 'Drop to import' : busy ? 'Validating CSV…' : 'Drag & drop your CSV'}</strong>
        <span>{busy ? 'You can replace or clear this file.' : <>or <span className="notification-csv__browse">choose a file</span></>}</span>
        <small>userId / email · 10,000 rows · 2 MiB</small>
      </button>
      <input ref={inputRef} aria-label="Recipient CSV file" type="file" accept=".csv,text/csv" onChange={onFileChange} hidden />
    </div>
    {filename && <div className="notification-csv__file">
      <FaFileCsv aria-hidden="true" />
      <span className="notification-csv__filename" title={filename}>{filename}</span>
      <button type="button" className="notification-csv__icon-button" aria-label="Clear CSV" onClick={clear}><FaTimes aria-hidden="true" /></button>
    </div>}
    {busy && <div role="status" className="notification-csv__muted">Reading and validating CSV…</div>}
    {error && <div role="alert" className="notification-csv__error"><FaExclamationCircle aria-hidden="true" /><span>{error}</span></div>}
    {report && <section className="notification-csv__result" aria-label="CSV import results">
      <div role="status" className={`notification-csv__status ${report.valid ? 'is-valid' : 'is-invalid'}`}>
        {report.valid ? <FaCheckCircle aria-hidden="true" /> : <FaExclamationCircle aria-hidden="true" />}
        <strong>{report.valid ? 'CSV ready for preview' : 'Import blocked'}</strong>
      </div>
      <dl className="notification-csv__stats">
        <div><dt>Matched users</dt><dd>{report.uniqueUsers.toLocaleString()}</dd></div>
        <div><dt>Duplicates</dt><dd>{report.duplicateRows.toLocaleString()}</dd></div>
        <div className={report.errorCount ? 'has-errors' : ''}><dt>Errors</dt><dd>{report.errorCount.toLocaleString()}</dd></div>
      </dl>
      <div className="notification-csv__muted">{report.rowCount.toLocaleString()} rows · {report.blankRows.toLocaleString()} blank ignored</div>
      {report.ignoredColumns.length > 0 && <div className="notification-csv__muted">Ignored columns: {report.ignoredColumns.join(', ')}</div>}
      {report.errorCount > 0 && <div className="notification-csv__issues">
        <div className="notification-csv__issue-heading">
          <span role="alert">Fix {report.errorCount.toLocaleString()} row error(s) to continue.</span>
          <button type="button" className="notification-csv__link" onClick={() => downloadCsv('notification-csv-errors.csv', [['line', 'code', 'message'], ...report.errors.map(e => [e.line, e.code, e.message])])}><FaDownload aria-hidden="true" /> Error report</button>
        </div>
        <div className="notification-csv__table-scroll" tabIndex={0} role="region" aria-label="CSV row errors">
          <table><thead><tr><th scope="col">Line</th><th scope="col">Issue</th></tr></thead><tbody>
            {report.errors.slice(0, 50).map((e, i) => <tr key={`${e.line}-${i}`}><th scope="row">{e.line}</th><td>{ISSUE_LABELS[e.code] ?? e.message}</td></tr>)}
          </tbody></table>
        </div>
        {report.errorCount > 50 && <div className="notification-csv__muted">First 50 shown · Download the full report.</div>}
      </div>}
      {report.sample.length > 0 && <details className="notification-csv__users">
        <summary>Matched users <span>{report.sample.length < report.uniqueUsers ? `First ${report.sample.length}` : report.sample.length}</span></summary>
        <ul>{report.sample.map(u => <li key={u.userId}><span>{u.name}</span><code>{u.userId}</code></li>)}</ul>
      </details>}
    </section>}
  </div>;
}
